import { useCallback } from 'react';
import { MEDIUM_OR_LARGE_COMPANY_SIZE_MIN } from '@hotjar/webapp/features/Activation/OnboardingPage/steps/OrganizationSteps/steps/PhoneNumberStep';
import { ProgressBar, tokens } from '@hotjar/hotsauce';
import styled from '@emotion/styled';
import { useMedia } from 'react-use';
import { getParameter } from '@hotjar/insights-common/lib/url';
import { useCurrentUser } from '@hotjar/user';
import { useCurrentSite } from '@hotjar/site';
import { useAccountExperiment, useUserExperiment } from '@hotjar/webapp/hooks/useExperiment';
import { STEPS } from '../SetupWizard/steps';
import { isUrlOnboarding, isUrlRegistration, isUrlSetup } from './utils';
import {
  ONBOARDING_STEPS,
  useOnboardingNavigation,
} from '../OnboardingPage/steps/OrganizationSteps/hooks/useOnboardingNavigation';

const RedProgressBar = styled(ProgressBar)`
  min-height: ${tokens.spacer.x1};
  > div {
    background-color: ${tokens.color.text_warning};
  }
`;

export const OnboardingProgressBar = (): JSX.Element => {
  const { step, getOrgDetails } = useOnboardingNavigation();
  const onMobile = useMedia('(max-width: 960px)');

  const { data: currentUser } = useCurrentUser();
  const { data: currentSite } = useCurrentSite();

  const canSeePhoneExperiment =
    !!currentUser?.id &&
    (getOrgDetails(currentUser?.id)?.organizationSizeMin || 0) >= MEDIUM_OR_LARGE_COMPANY_SIZE_MIN;

  const { inVariant1: inPhoneNumberExperiment } = useUserExperiment(
    'av_onboarding_phone_number_v3',
    currentUser,
    canSeePhoneExperiment,
  );

  const { inVariant1: shouldShowInviteTeammateStep } = useAccountExperiment(
    'stp_invite_teammate_registration_step_v2',
    currentSite?.account,
    !!currentSite?.account && !onMobile,
  );

  /*
   * To add new steps, first add it here to the array and then continue
   * on progressBarCurrentStep method.
   */
  const getTotalSteps = useCallback(() => {
    const isOnboardingJourney = !!getParameter(window.location.href, 'onboarding')?.length;

    return [
      'REGISTER',
      ONBOARDING_STEPS.ORG_NAME,
      ONBOARDING_STEPS.ORG_SIZE,
      ONBOARDING_STEPS.ROLE,
      ONBOARDING_STEPS.GOAL,
      ONBOARDING_STEPS.EXPERTISE,
      ...(inPhoneNumberExperiment ? [ONBOARDING_STEPS.PHONE_NUMBER] : []),
      STEPS.SITE_URL,
      ...(shouldShowInviteTeammateStep ? [STEPS.INVITE_TEAMMATE] : []),
      ...(!isOnboardingJourney ? [STEPS.INSTALLATION_METHOD] : []),
      'FINISH',
    ];
  }, [inPhoneNumberExperiment, shouldShowInviteTeammateStep]);

  const stepsToPercentage = (currentStep: number | null) => {
    if (currentStep === null) {
      return 0;
    }
    return Math.round(((currentStep + 1) / getTotalSteps().length) * 100);
  };

  /*
   * Match a routing condition to the index of the step in the steps array
   */
  const progressBarCurrentStep = useCallback(() => {
    if (isUrlRegistration()) {
      return getTotalSteps().indexOf('REGISTER');
    }

    if (isUrlOnboarding() && step) {
      return getTotalSteps().indexOf(step) !== -1 ? getTotalSteps().indexOf(step) : 1;
    }

    if (isUrlSetup()) {
      const currentSetupStep = getParameter(window.location.href, 'step');
      if (currentSetupStep === STEPS.SITE_URL) {
        return getTotalSteps().indexOf(STEPS.SITE_URL);
      }
      if (currentSetupStep === STEPS.INVITE_TEAMMATE) {
        return getTotalSteps().indexOf(STEPS.INVITE_TEAMMATE);
      }

      return getTotalSteps().indexOf(STEPS.INSTALLATION_METHOD);
    }

    return null;
  }, [step, getTotalSteps]);

  return (
    <div aria-hidden>
      <RedProgressBar currentValue={stepsToPercentage(progressBarCurrentStep())} />
    </div>
  );
};
